<div class="entity-history-container">

  <!-- ── Header ─────────────────────────────────────────────────── -->
  <div class="eh-header">
    <div class="eh-header-left">
      <h2 class="eh-entity-name">{{ entityName }} <span class="eh-entity-id">({{ entityId }})</span></h2>
      <p class="eh-subtitle">{{ entityType | titlecase }} History</p>
      <div class="eh-stats-bar" *ngIf="flattenedData.length > 0">
        <span>{{ totalChanges }} change(s)</span>
        <span class="eh-stats-sep">&bull;</span>
        <span>{{ sessionCount }} session(s)</span>
        <span class="eh-stats-sep">&bull;</span>
        <span>Last modified {{ lastModified | officeTime:'medium' }}</span>
      </div>
    </div>
    <button class="eh-mode-toggle" (click)="toggleDisplayMode()" *ngIf="flattenedData.length > 0">
      <i class="pi" [ngClass]="displayMode === 'grouped' ? 'pi-list' : 'pi-th-large'"></i>
      {{ displayMode === 'grouped' ? 'View as flat list' : 'View as sessions' }}
    </button>
  </div>

  <!-- ── Filter bar ─────────────────────────────────────────────── -->
  <div class="eh-filter-bar" *ngIf="flattenedData.length > 0">
    <div class="eh-filter-row">
      <!-- Field -->
      <div class="eh-filter-item">
        <label>Field</label>
        <p-dropdown
          [options]="fieldOptions"
          [(ngModel)]="selectedField"
          optionLabel="label"
          optionValue="value"
          placeholder="All Fields"
          (onChange)="onFilterChange()"
          [showClear]="true">
        </p-dropdown>
      </div>
      <!-- User -->
      <div class="eh-filter-item">
        <label>User</label>
        <p-dropdown
          [options]="userOptions"
          [(ngModel)]="selectedUser"
          optionLabel="label"
          optionValue="value"
          placeholder="All Users"
          (onChange)="onFilterChange()"
          [showClear]="true">
        </p-dropdown>
      </div>
      <!-- Action -->
      <div class="eh-filter-item">
        <label>Action</label>
        <p-dropdown
          [options]="actionOptions"
          [(ngModel)]="selectedAction"
          optionLabel="label"
          optionValue="value"
          placeholder="All Actions"
          (onChange)="onFilterChange()"
          [showClear]="true">
        </p-dropdown>
      </div>
      <!-- Search -->
      <div class="eh-filter-item eh-search-item">
        <label>&nbsp;</label>
        <span class="p-input-icon-left eh-search-wrap">
          <i class="pi pi-search"></i>
          <input
            type="text"
            pInputText
            placeholder="Search changes by user, field, or value..."
            [(ngModel)]="searchTerm"
            (input)="onSearchChange()"
            class="eh-search-input"
          />
          <button *ngIf="searchTerm" class="eh-search-clear" (click)="clearSearch()">
            <i class="pi pi-times"></i>
          </button>
        </span>
      </div>
    </div>

    <div class="eh-filter-row eh-filter-actions">
      <p-checkbox
        [binary]="true"
        [ngModel]="displayMode === 'grouped'"
        (ngModelChange)="displayMode = $event ? 'grouped' : 'flat'; applyFilters()"
        label="Group by session">
      </p-checkbox>
      <span *ngIf="hasActiveFilters" class="eh-results-text">
        Showing {{ filteredData.length }} of {{ flattenedData.length }} changes
      </span>
      <div class="eh-action-btns">
        <p-button
          label="Clear filters"
          icon="pi pi-times"
          styleClass="p-button-outlined p-button-sm"
          (click)="clearAllFilters()"
          [disabled]="!hasActiveFilters">
        </p-button>
        <p-button
          label="Reset sorting"
          icon="pi pi-sort-alt-slash"
          styleClass="p-button-outlined p-button-sm p-button-success"
          (click)="resetSorting()"
          [disabled]="isDefaultSort">
        </p-button>
      </div>
    </div>
  </div>

  <!-- ── Grouped / session-card view ───────────────────────────── -->
  <div class="eh-sessions" *ngIf="displayMode === 'grouped' && flattenedData.length > 0">

    <div *ngIf="groupedData.length === 0" class="eh-empty">
      <i class="pi pi-search"></i>
      <p>No changes match your filters.</p>
    </div>

    <div class="eh-session-card" *ngFor="let session of groupedData">

      <!-- Session header -->
      <div class="eh-session-header" (click)="toggleSession(session.sessionKey)">
        <div class="eh-session-icon">
          <i class="pi pi-calendar"></i>
        </div>
        <div class="eh-session-meta">
          <span class="eh-session-date">{{ session.date | officeTime:'medium' }}</span>
          <span class="eh-session-divider">|</span>
          <span class="eh-session-user"><i class="pi pi-user"></i> {{ session.userName }}</span>
          <p-tag
            *ngIf="session.automation"
            [value]="'via ' + session.automation"
            severity="warning"
            styleClass="eh-source-tag"
            pTooltip="Performed by a CMS automation"></p-tag>
          <p-tag *ngIf="session.source" [value]="session.source" severity="info" styleClass="eh-source-tag"></p-tag>
          <span class="eh-session-divider">|</span>
          <span class="eh-session-count">{{ session.changes.length }} field(s) changed</span>
        </div>
        <button class="eh-session-toggle">
          {{ session.changes.length }} change(s)
          <i class="pi" [ngClass]="session.isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>
      </div>

      <!-- Session body -->
      <div class="eh-session-body" *ngIf="session.isExpanded">
        <table class="eh-changes-table">
          <thead>
            <tr>
              <th class="col-field">Field</th>
              <th class="col-change">Change</th>
              <th class="col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let change of session.changes">

              <!-- Main change row -->
              <tr [class.eh-row-highlighted]="change.isHighlighted">

                <!-- Field -->
                <td class="eh-field-cell">
                  <span class="eh-field-name">{{ change.fieldName }}</span>
                  <span class="eh-field-type">{{ change.fieldType | titlecase }}</span>
                </td>

                <!-- Change -->
                <td class="eh-change-cell">
                  <div class="eh-change-content">
                    <ng-container [ngSwitch]="change.fieldType">

                      <!-- String: inline char diff -->
                      <ng-container *ngSwitchCase="'string'">
                        <ng-container *ngIf="change.inlineDiffHtml; else stringPlain">
                          <span class="eh-string-box eh-string-before">
                            <span [innerHTML]="change.originalValueHtml"></span>
                          </span>
                          <i class="pi pi-arrow-right eh-arrow"></i>
                          <span class="eh-string-box eh-string-after">
                            <span [innerHTML]="change.inlineDiffHtml"></span>
                          </span>
                        </ng-container>
                        <ng-template #stringPlain>
                          <span class="eh-string-box eh-string-after" [innerHTML]="change.newValueHtml"></span>
                        </ng-template>
                      </ng-container>

                      <!-- Boolean: pills -->
                      <ng-container *ngSwitchCase="'boolean'">
                        <span class="eh-bool-pill" [ngClass]="change.originalValue ? 'eh-bool-true' : 'eh-bool-false'">
                          {{ change.originalValue }}
                        </span>
                        <i class="pi pi-arrow-right eh-arrow"></i>
                        <span class="eh-bool-pill" [ngClass]="change.newValue ? 'eh-bool-true' : 'eh-bool-false'">
                          {{ change.newValue }}
                        </span>
                      </ng-container>

                      <!-- Array: chip diff -->
                      <ng-container *ngSwitchCase="'array'">
                        <ng-container *ngIf="change.arrayDiffHtml; else arrayPlain">
                          <span [innerHTML]="change.arrayDiffHtml"></span>
                        </ng-container>
                        <ng-template #arrayPlain>
                          <span [innerHTML]="change.newValueHtml"></span>
                        </ng-template>
                      </ng-container>

                      <!-- Object: expandable summary -->
                      <ng-container *ngSwitchCase="'object'">
                        <div class="eh-object-summary" (click)="toggleRowExpand(change.id)">
                          <span class="eh-object-icon">&#123;...&#125;</span>
                          <div class="eh-object-text">
                            <span>Object value changed</span>
                            <span class="eh-object-hint">Click to view changes</span>
                          </div>
                          <i class="pi eh-object-chevron"
                             [ngClass]="isRowExpanded(change.id) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                        </div>
                      </ng-container>

                      <!-- Date: formatted before → after -->
                      <ng-container *ngSwitchCase="'date'">
                        <span class="eh-date-val eh-val-old">{{ change.originalValue | officeTime:'medium' }}</span>
                        <i class="pi pi-arrow-right eh-arrow"></i>
                        <span class="eh-date-val eh-val-new">{{ change.newValue | officeTime:'medium' }}</span>
                      </ng-container>

                      <!-- Ref: resolved entity links -->
                      <ng-container *ngSwitchCase="'ref'">
                        <span *ngIf="change.hasOriginalValue" [innerHTML]="change.originalValueHtml" class="eh-ref-chip"></span>
                        <i *ngIf="change.hasOriginalValue" class="pi pi-arrow-right eh-arrow"></i>
                        <span [innerHTML]="change.newValueHtml" class="eh-ref-chip"></span>
                      </ng-container>

                      <!-- Number -->
                      <ng-container *ngSwitchCase="'number'">
                        <span class="eh-bool-pill eh-val-old">{{ change.originalValue }}</span>
                        <i class="pi pi-arrow-right eh-arrow"></i>
                        <span class="eh-bool-pill eh-val-new">{{ change.newValue }}</span>
                      </ng-container>

                      <!-- Fallback -->
                      <ng-container *ngSwitchDefault>
                        <span *ngIf="change.hasOriginalValue" [innerHTML]="change.originalValueHtml" class="eh-val-old"></span>
                        <i *ngIf="change.hasOriginalValue" class="pi pi-arrow-right eh-arrow"></i>
                        <span [innerHTML]="change.newValueHtml" class="eh-val-new"></span>
                      </ng-container>

                    </ng-container>
                  </div>

                  <!-- "View before / after" — not for objects -->
                  <button *ngIf="change.fieldType !== 'object'" class="eh-view-toggle"
                          (click)="toggleRowExpand(change.id)">
                    {{ isRowExpanded(change.id) ? 'Hide' : 'View before / after' }}
                  </button>
                </td>

                <!-- Action -->
                <td class="eh-action-cell">
                  <p-tag [value]="change.action" [severity]="getActionSeverity(change.action)"></p-tag>
                  <i class="pi eh-row-chevron"
                     [ngClass]="isRowExpanded(change.id) ? 'pi-chevron-up' : 'pi-chevron-down'"
                     (click)="toggleRowExpand(change.id)"></i>
                </td>
              </tr>

              <!-- Before / after expand row -->
              <tr *ngIf="isRowExpanded(change.id)" class="eh-expand-row">
                <td></td>
                <td colspan="2">
                  <div class="eh-expand-panel">
                    <div class="eh-expand-col">
                      <span class="eh-expand-label">Before</span>
                      <pre class="json-block" [innerHTML]="change.originalValueHtml || '<span class=\'eh-null\'>empty</span>'"></pre>
                    </div>
                    <div class="eh-expand-col">
                      <span class="eh-expand-label">After</span>
                      <pre class="json-block" [innerHTML]="change.newValueHtml || '<span class=\'eh-null\'>empty</span>'"></pre>
                    </div>
                  </div>
                </td>
              </tr>

            </ng-container>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- ── Flat list view ─────────────────────────────────────────── -->
  <div class="table-container" *ngIf="displayMode === 'flat' && flattenedData.length > 0">
    <p-table
      [value]="filteredData"
      styleClass="p-datatable-gridlines p-datatable-striped history-table"
      [paginator]="true"
      [rows]="20"
      [rowsPerPageOptions]="[10, 20, 50, 100]"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} changes"
      responsiveLayout="scroll"
      [scrollable]="true"
      scrollHeight="600px"
      [sortField]="sortField"
      [sortOrder]="sortOrder"
      (onSort)="onSort($event)">

      <ng-template pTemplate="header">
        <tr>
          <th pSortableColumn="date" style="width:160px">Date <p-sortIcon field="date"></p-sortIcon></th>
          <th pSortableColumn="userName" style="width:120px">User <p-sortIcon field="userName"></p-sortIcon></th>
          <th pSortableColumn="source" style="width:100px">Source <p-sortIcon field="source"></p-sortIcon></th>
          <th pSortableColumn="fieldName" style="width:160px">Field <p-sortIcon field="fieldName"></p-sortIcon></th>
          <th>Change</th>
          <th pSortableColumn="action" style="width:120px">Action <p-sortIcon field="action"></p-sortIcon></th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-change>
        <tr [class.highlight-row]="change.isHighlighted">

          <!-- Date -->
          <td>
            <div class="date-cell">
              <span class="date-main">{{ change.date | officeTime:'short' }}</span>
              <span class="date-time">{{ change.date | officeTime:'shortTime' }}</span>
            </div>
          </td>

          <!-- User -->
          <td>
            <div class="user-cell">
              <i class="pi pi-user user-icon"></i>
              <span class="user-name" [title]="attributionLabel(change)">{{ change.userName }}</span>
              <span *ngIf="change.automation" class="automation-name" [title]="attributionLabel(change)">via {{ change.automation }}</span>
            </div>
          </td>

          <!-- Source -->
          <td>
            <p-tag *ngIf="change.source" [value]="change.source" severity="info" styleClass="source-tag"></p-tag>
            <span *ngIf="!change.source" class="no-source">-</span>
          </td>

          <!-- Field -->
          <td class="eh-field-cell">
            <span class="eh-field-name">{{ change.fieldName }}</span>
            <span class="eh-field-type">{{ change.fieldType | titlecase }}</span>
          </td>

          <!-- Change (same renderers as grouped view) -->
          <td class="eh-change-cell">
            <div class="eh-change-content">
              <ng-container [ngSwitch]="change.fieldType">

                <ng-container *ngSwitchCase="'string'">
                  <ng-container *ngIf="change.inlineDiffHtml; else flatStringPlain">
                    <span class="eh-string-box eh-string-before"><span [innerHTML]="change.originalValueHtml"></span></span>
                    <i class="pi pi-arrow-right eh-arrow"></i>
                    <span class="eh-string-box eh-string-after"><span [innerHTML]="change.inlineDiffHtml"></span></span>
                  </ng-container>
                  <ng-template #flatStringPlain>
                    <span class="eh-string-box eh-string-after" [innerHTML]="change.newValueHtml"></span>
                  </ng-template>
                </ng-container>

                <ng-container *ngSwitchCase="'boolean'">
                  <span class="eh-bool-pill" [ngClass]="change.originalValue ? 'eh-bool-true' : 'eh-bool-false'">{{ change.originalValue }}</span>
                  <i class="pi pi-arrow-right eh-arrow"></i>
                  <span class="eh-bool-pill" [ngClass]="change.newValue ? 'eh-bool-true' : 'eh-bool-false'">{{ change.newValue }}</span>
                </ng-container>

                <ng-container *ngSwitchCase="'array'">
                  <span *ngIf="change.arrayDiffHtml; else flatArrayPlain" [innerHTML]="change.arrayDiffHtml"></span>
                  <ng-template #flatArrayPlain><span [innerHTML]="change.newValueHtml"></span></ng-template>
                </ng-container>

                <ng-container *ngSwitchCase="'object'">
                  <div class="eh-object-summary" (click)="toggleRowExpand(change.id)">
                    <span class="eh-object-icon">&#123;...&#125;</span>
                    <div class="eh-object-text">
                      <span>Object value changed</span>
                      <span class="eh-object-hint">Click to view changes</span>
                    </div>
                    <i class="pi eh-object-chevron" [ngClass]="isRowExpanded(change.id) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                  </div>
                </ng-container>

                <ng-container *ngSwitchCase="'date'">
                  <span class="eh-date-val eh-val-old">{{ change.originalValue | officeTime:'medium' }}</span>
                  <i class="pi pi-arrow-right eh-arrow"></i>
                  <span class="eh-date-val eh-val-new">{{ change.newValue | officeTime:'medium' }}</span>
                </ng-container>

                <ng-container *ngSwitchCase="'ref'">
                  <span *ngIf="change.hasOriginalValue" [innerHTML]="change.originalValueHtml" class="eh-ref-chip"></span>
                  <i *ngIf="change.hasOriginalValue" class="pi pi-arrow-right eh-arrow"></i>
                  <span [innerHTML]="change.newValueHtml" class="eh-ref-chip"></span>
                </ng-container>

                <ng-container *ngSwitchCase="'number'">
                  <span class="eh-bool-pill eh-val-old">{{ change.originalValue }}</span>
                  <i class="pi pi-arrow-right eh-arrow"></i>
                  <span class="eh-bool-pill eh-val-new">{{ change.newValue }}</span>
                </ng-container>

                <ng-container *ngSwitchDefault>
                  <span *ngIf="change.hasOriginalValue" [innerHTML]="change.originalValueHtml" class="eh-val-old"></span>
                  <i *ngIf="change.hasOriginalValue" class="pi pi-arrow-right eh-arrow"></i>
                  <span [innerHTML]="change.newValueHtml" class="eh-val-new"></span>
                </ng-container>

              </ng-container>
            </div>
            <button *ngIf="change.fieldType !== 'object'" class="eh-view-toggle" (click)="toggleRowExpand(change.id)">
              {{ isRowExpanded(change.id) ? 'Hide' : 'View before / after' }}
            </button>
            <!-- Inline before/after panel (flat list) -->
            <div *ngIf="isRowExpanded(change.id)" class="eh-expand-panel eh-expand-inline">
              <div class="eh-expand-col">
                <span class="eh-expand-label">Before</span>
                <pre class="json-block" [innerHTML]="change.originalValueHtml || '<span class=\'eh-null\'>empty</span>'"></pre>
              </div>
              <div class="eh-expand-col">
                <span class="eh-expand-label">After</span>
                <pre class="json-block" [innerHTML]="change.newValueHtml || '<span class=\'eh-null\'>empty</span>'"></pre>
              </div>
            </div>
          </td>

          <!-- Action -->
          <td class="eh-action-cell">
            <p-tag [value]="change.action" [severity]="getActionSeverity(change.action)"></p-tag>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="empty-message">
            <div class="empty-content">
              <i class="pi pi-search empty-icon"></i>
              <h4>No changes found</h4>
              <p>Try adjusting your search criteria or filters</p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  <!-- ── No data ────────────────────────────────────────────────── -->
  <div class="no-data-container" *ngIf="flattenedData.length === 0">
    <div class="no-data-content">
      <i class="pi pi-history no-data-icon"></i>
      <h4>No History Available</h4>
      <p>There are no recorded changes for this entity yet.</p>
    </div>
  </div>

  <!-- ── Legend ─────────────────────────────────────────────────── -->
  <div class="eh-legend" *ngIf="flattenedData.length > 0">
    <span class="eh-legend-label">Legend:</span>
    <span class="diff-chip diff-added">+ Added</span>
    <span class="diff-chip diff-removed">- Removed</span>
    <span class="diff-chip diff-unchanged">= Unchanged</span>
    <span class="eh-legend-sep">|</span>
    <span class="eh-legend-label">Action types:</span>
    <p-tag value="add" severity="success"></p-tag>
    <p-tag value="remove" severity="danger"></p-tag>
    <p-tag value="replace" severity="warning"></p-tag>
  </div>

</div>
